import { useEffect, useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';

interface ActiveLesson {
  id: string; ders_tarihi: string; ders_saati: string | null; brans: string | null; ders_durumu: string | null;
  ogrenci?: { ogrenci_adi: string }; ogretmen?: { ad_soyad: string };
}
interface Application {
  id: string; ogrenci_adi: string; brans?: string; sinif?: string; kaynak?: string; durum: string | null;
}
interface PendingPayment {
  id: string; tutar: number; dekont_tarihi: string; odeme_durumu: string | null;
  ogrenci?: { ogrenci_adi: string }; ogretmen?: { ad_soyad: string };
}
interface TopTeacher {
  id: string; ad_soyad: string; branslar: string[] | null; ogrenci_sayisi: number;
}

const statusColors: Record<string, string> = {
  Yapıldı: 'bg-emerald-100 text-emerald-700',
  Planlandı: 'bg-blue-100 text-blue-700',
  Ertelendi: 'bg-yellow-100 text-yellow-700',
  'Yeni Başvuru': 'bg-blue-100 text-blue-700',
  'Öğretmen Aranıyor': 'bg-amber-100 text-amber-700',
  'Öğretmene Yönlendirildi': 'bg-indigo-100 text-indigo-700',
  'Deneme Dersi Planlandı': 'bg-cyan-100 text-cyan-700',
  'Aktif Derse Başladı': 'bg-emerald-100 text-emerald-700',
  Takipte: 'bg-orange-100 text-orange-700',
  Onaylandı: 'bg-emerald-100 text-emerald-700',
  'Dekont Bekleniyor': 'bg-amber-100 text-amber-700',
  'Yönetici Kontrolünde': 'bg-purple-100 text-purple-700',
  'Dekont Geldi': 'bg-blue-100 text-blue-700',
  İptal: 'bg-red-100 text-red-600',
};

function SectionCard({ title, icon, badge, children }: { title: string; icon: string; badge?: number; children: ReactNode }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 flex items-center justify-center">
            <i className={`${icon} text-[#1a2f5a] text-base`} />
          </div>
          <p className="text-sm font-semibold text-gray-800">{title}</p>
          {badge !== undefined && <span className="text-xs bg-[#1a2f5a] text-white font-semibold px-2 py-0.5 rounded-full">{badge}</span>}
        </div>
      </div>
      <div>{children}</div>
    </div>
  );
}

export default function DashboardLists() {
  const [todayLessons, setTodayLessons] = useState<ActiveLesson[]>([]);
  const [recentApps, setRecentApps] = useState<Application[]>([]);
  const [pendingPayments, setPendingPayments] = useState<PendingPayment[]>([]);
  const [topTeachers, setTopTeachers] = useState<TopTeacher[]>([]);

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    const fetchAll = async () => {
      const [{ data: tl }, { data: apps }, { data: pp }, { data: teachers }] = await Promise.all([
        supabase.from('egtpanel_aktif_dersler')
          .select('id, ders_tarihi, ders_saati, brans, ders_durumu, ogrenci:egtpanel_ogrenciler(ogrenci_adi), ogretmen:egtpanel_ogretmenler(ad_soyad)')
          .eq('ders_tarihi', today).order('ders_saati').limit(5),
        supabase.from('egtpanel_basvurular')
          .select('id, ogrenci_adi, brans, sinif, kaynak, durum').order('basvuru_tarihi', { ascending: false }).limit(5),
        supabase.from('egtpanel_odemeler')
          .select('id, tutar, dekont_tarihi, odeme_durumu, ogrenci:egtpanel_ogrenciler(ogrenci_adi), ogretmen:egtpanel_ogretmenler(ad_soyad)')
          .eq('yonetici_onay', false).neq('odeme_durumu', 'İptal').order('olusturma_tarihi', { ascending: false }).limit(4),
        supabase.from('egtpanel_ogretmenler').select('id, ad_soyad, branslar').eq('aktif', true).limit(10),
      ]);
      setTodayLessons((tl || []) as ActiveLesson[]);
      setRecentApps((apps || []) as Application[]);
      setPendingPayments((pp || []) as PendingPayment[]);

      // Enrich teachers with student count
      const enriched = await Promise.all((teachers || []).map(async t => {
        const { count } = await supabase.from('egtpanel_ogrenciler').select('id', { count: 'exact', head: true }).eq('atanan_ogretmen_id', t.id).eq('aktif', true);
        return { ...t, ogrenci_sayisi: count || 0 };
      }));
      setTopTeachers(enriched.sort((a, b) => b.ogrenci_sayisi - a.ogrenci_sayisi).slice(0, 4));
    };
    fetchAll();
  }, []);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SectionCard title="Bugünkü Dersler" icon="ri-calendar-2-line" badge={todayLessons.length}>
        <ul>
          {todayLessons.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-gray-400">Bugün planlanmış ders yok</li>
          ) : todayLessons.map((lesson, i) => {
            const ogr = lesson.ogrenci as {ogrenci_adi:string}|undefined;
            const ogrt = lesson.ogretmen as {ad_soyad:string}|undefined;
            return (
              <li key={lesson.id} className={`flex items-center gap-3 px-5 py-3 ${i < todayLessons.length - 1 ? 'border-b border-gray-50' : ''}`}>
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#f0f4ff] flex-shrink-0">
                  <span className="text-[#1a2f5a] text-xs font-bold">{lesson.ders_saati?.slice(0,5) || '—'}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{ogr?.ogrenci_adi || '—'}</p>
                  <p className="text-xs text-gray-500 truncate"><span className="text-[#7c6af5]">{lesson.brans || '—'}</span> · {ogrt?.ad_soyad || '—'}</p>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[lesson.ders_durumu || ''] ?? 'bg-gray-100 text-gray-600'}`}>{lesson.ders_durumu || 'Planlandı'}</span>
              </li>
            );
          })}
        </ul>
      </SectionCard>

      <SectionCard title="Son Gelen Başvurular" icon="ri-file-list-3-line" badge={recentApps.length}>
        <ul>
          {recentApps.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-gray-400">Henüz başvuru yok</li>
          ) : recentApps.map((app, i) => (
            <li key={app.id} className={`flex items-center gap-3 px-5 py-3 ${i < recentApps.length - 1 ? 'border-b border-gray-50' : ''}`}>
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-[#1a2f5a] to-[#3b5bdb] flex-shrink-0">
                <span className="text-white text-xs font-bold">{app.ogrenci_adi?.charAt(0) || '?'}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{app.ogrenci_adi}</p>
                <p className="text-xs text-gray-500 truncate">{[app.brans, app.sinif, app.kaynak].filter(Boolean).join(' · ')}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${statusColors[app.durum || ''] ?? 'bg-gray-100 text-gray-600'}`}>{app.durum || '—'}</span>
            </li>
          ))}
        </ul>
      </SectionCard>

      <SectionCard title="Ödeme Bekleyen Kayıtlar" icon="ri-time-line" badge={pendingPayments.length}>
        {pendingPayments.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-gray-400">
            <i className="ri-checkbox-circle-line text-2xl text-emerald-400 block mb-1" />Bekleyen ödeme yok
          </div>
        ) : (
          <ul>
            {pendingPayments.map((p, i) => {
              const ogr = p.ogrenci as {ogrenci_adi:string}|undefined;
              const ogrt = p.ogretmen as {ad_soyad:string}|undefined;
              return (
                <li key={p.id} className={`flex items-center gap-3 px-5 py-3 ${i < pendingPayments.length - 1 ? 'border-b border-gray-50' : ''}`}>
                  <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-amber-50 flex-shrink-0">
                    <i className="ri-money-dollar-circle-line text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{ogr?.ogrenci_adi || '—'}</p>
                    <p className="text-xs text-gray-500 truncate">{ogrt?.ad_soyad || '—'} · {p.dekont_tarihi}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-bold text-gray-800">₺{Number(p.tutar).toLocaleString('tr-TR')}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[p.odeme_durumu || ''] ?? 'bg-gray-100 text-gray-600'}`}>{p.odeme_durumu || '—'}</span>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </SectionCard>

      <SectionCard title="En Aktif Öğretmenler" icon="ri-user-star-line">
        <ul>
          {topTeachers.length === 0 ? (
            <li className="px-5 py-8 text-center text-sm text-gray-400">Öğretmen bulunamadı</li>
          ) : topTeachers.map((teacher, i) => (
            <li key={teacher.id} className={`flex items-center gap-3 px-5 py-3 ${i < topTeachers.length - 1 ? 'border-b border-gray-50' : ''}`}>
              <div className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-br from-[#7c6af5] to-[#a78bfa] flex-shrink-0">
                <span className="text-white text-xs font-bold">{teacher.ad_soyad.charAt(0)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{teacher.ad_soyad}</p>
                <p className="text-xs text-gray-500 truncate">{teacher.branslar?.slice(0, 2).join(', ') || '—'}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-bold text-[#1a2f5a]">{teacher.ogrenci_sayisi} öğrenci</p>
              </div>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}